/**
 * Access control — US-A01 role matrix.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS CONVENIENCE, NOT SECURITY.
 *
 * The matrix below hides controls a role cannot use, so the UI is honest about
 * what the person in front of it may do. It is *not* the authorisation
 * boundary. Row-level security in Postgres is (docs/03-technical-architecture
 * §4.3 and §10): every admin policy is gated on `is_admin(<role array>)`, and
 * anything financial, media-URL-minting, or moderation-state-changing is written
 * only by an edge function holding the service role.
 *
 * US-A01 AC4 — "given a Moderator, when they call a content-management endpoint
 * directly, then the server refuses" — is satisfied by RLS, not by this file.
 * If the two ever disagree, RLS is right and this file is a bug.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The matrix is DATA. Every row of the US-A01 table appears once, here, and
 * nowhere else. Pages ask `<CanAccess>` / `useCan()`; they never test the role.
 */
import type { AccessControlProvider } from '@refinedev/core'
import type { AdminRole } from '@/types'

const ALL: readonly AdminRole[] = ['super_admin', 'content_manager', 'moderator', 'support']
const CONTENT: readonly AdminRole[] = ['super_admin', 'content_manager']
const MODERATION: readonly AdminRole[] = ['super_admin', 'moderator']
const BILLING: readonly AdminRole[] = ['super_admin', 'support']
const SUPER: readonly AdminRole[] = ['super_admin']

/** The action vocabulary Refine passes us, plus the custom verbs we raise. */
export type Action =
  | 'list'
  | 'show'
  | 'create'
  | 'edit'
  | 'delete'
  | 'upload'
  | 'publish'
  | 'moderate'
  | 'ban'
  | 'grant'
  | 'revoke'
  | 'send'
  | 'export'
  | 'field'

type ResourceRules = {
  /** Applied when the action has no explicit entry. */
  default: readonly AdminRole[]
  actions?: Partial<Record<Action, readonly AdminRole[]>>
}

/**
 * resource × action → allowed roles.
 *
 * Capability column of the US-A01 table → the rows it maps to:
 *
 *   Subjects and content CRUD    → subjects, content_items, reference_data
 *   Media upload                 → content_items:upload, ingest
 *   Publish / unpublish          → subjects:publish, content_items:publish
 *   Moderation queue             → moderation
 *   Ban / unban users            → users:ban
 *   View users                   → users:list, users:show
 *   Grant / revoke subscriptions → users:grant, users:revoke, plans (read)
 *   Send notifications           → notifications
 *   Edit CMS pages               → cms_pages, faq_items
 *   Dashboard                    → dashboard
 *   Manage admin users           → admin_users
 *   Audit log                    → audit_log
 */
export const ACCESS_MATRIX: Record<string, ResourceRules> = {
  // ── Dashboard: every role ────────────────────────────────────────────────
  dashboard: { default: ALL },

  // ── Content: Super Admin + Content Manager ───────────────────────────────
  subjects: {
    default: CONTENT,
    actions: { list: CONTENT, show: CONTENT, publish: CONTENT, upload: CONTENT },
  },
  content_items: {
    default: CONTENT,
    actions: { upload: CONTENT, publish: CONTENT },
  },
  ingest: { default: CONTENT },
  // Streams, programs and cities are content-shaped reference data (W-091).
  streams: { default: CONTENT },
  programs: { default: CONTENT },
  cities: { default: CONTENT },

  // ── Moderation: Super Admin + Moderator ──────────────────────────────────
  reports: { default: MODERATION, actions: { moderate: MODERATION } },
  posts: { default: MODERATION, actions: { moderate: MODERATION, delete: MODERATION } },

  // ── Users: everyone reads; only Moderator bans; only Support grants ──────
  users: {
    default: ALL,
    actions: {
      list: ALL,
      show: ALL,
      export: ALL,
      ban: MODERATION,
      moderate: MODERATION,
      // Deactivating an account is a support action with a recorded reason
      // (US-A06 AC5); it is not a moderation ban.
      edit: BILLING,
      delete: SUPER,
      grant: BILLING,
      revoke: BILLING,
    },
  },

  // ── Subscriptions and money: Super Admin + Support ───────────────────────
  // Plans are priced by the Super Admin (US-A11 is written for that role);
  // Support reads them so the grant dialog can offer a plan.
  plans: {
    default: SUPER,
    actions: { list: BILLING, show: BILLING },
  },
  subscriptions: { default: BILLING, actions: { grant: BILLING, revoke: BILLING } },
  transactions: { default: BILLING, actions: { list: BILLING, show: BILLING, export: BILLING } },

  // ── Notifications: Super Admin + Content Manager ─────────────────────────
  notifications: { default: CONTENT, actions: { send: CONTENT } },

  // ── CMS: Super Admin + Content Manager ───────────────────────────────────
  cms_pages: { default: CONTENT, actions: { publish: CONTENT } },
  faq_items: { default: CONTENT },

  // ── Support inbox: Support desk plus the Super Admin ─────────────────────
  support_messages: { default: BILLING },

  // ── System: Super Admin only ─────────────────────────────────────────────
  admin_users: { default: SUPER },
  audit_log: {
    default: SUPER,
    // AC3: append-only. Nobody, not even the Super Admin, writes or edits it
    // from the UI; entries are inserted by edge functions.
    actions: { create: [], edit: [], delete: [] },
  },
}

/** Pure matrix lookup — the unit under test. */
export function can(role: AdminRole | undefined, resource: string, action: Action): boolean {
  if (!role) return false
  const rules = ACCESS_MATRIX[resource]
  if (!rules) return false
  const allowed = rules.actions?.[action] ?? rules.default
  return allowed.includes(role)
}

/** Every resource the given role may see at all — drives the sidebar (W-002). */
export function visibleResources(role: AdminRole | undefined): string[] {
  if (!role) return []
  return Object.keys(ACCESS_MATRIX).filter(resource => can(role, resource, 'list'))
}

/**
 * @param getRole resolves the signed-in administrator's role. Injected so the
 * provider stays testable and so the role can come from a cache rather than a
 * round-trip on every `useCan()`.
 */
export function createAccessControlProvider(
  getRole: () => Promise<AdminRole | undefined>,
): AccessControlProvider {
  return {
    can: async ({ resource, action, params }) => {
      const role = await getRole()
      if (!role) {
        return { can: false, reason: 'not_authenticated' }
      }
      // Refine calls with resource names; nested resources pass the parent name
      // in `params.resource.meta.parent`, which we do not use.
      const name = params?.resource?.name ?? resource ?? ''
      const allowed = can(role, name, action as Action)
      return allowed
        ? { can: true }
        : { can: false, reason: `role_${role}_may_not_${action}_${name}` }
    },
    options: {
      buttons: { enableAccessControl: true, hideIfUnauthorized: true },
    },
  }
}
